import { Stack, Tooltip, Typography, type TooltipProps } from '@mui/material';
import classnames from 'classnames';
import React from 'react';

interface TooltipContentProps {
  isAuthorized?: boolean;
  isFeatureSupported?: boolean;
  title: string | null;
  overrideTitle?: boolean;
}

const TooltipContent = (props: TooltipContentProps) => {
  const shouldShowTitle =
    props.title &&
    (!props.overrideTitle || (props.isAuthorized && props.isFeatureSupported));

  return (
    <Stack
      direction='column'
      sx={{
        marginX: '4px',
        gap: '4px',
      }}
    >
      {shouldShowTitle && (
        <Typography
          className={classnames('text-xs', {
            'font-semibold': !props.isAuthorized || !props.isFeatureSupported,
          })}
          sx={{
            color: 'var(--mui-palette-Tooltip-text)',
          }}
        >
          {props.title}
        </Typography>
      )}
      {!props.isAuthorized && (
        <Typography
          className='text-xs'
          sx={{
            width: '176px',
            color: 'var(--mui-palette-Tooltip-text)',
          }}
        >
          An admin has blocked this action for your current user role.
        </Typography>
      )}
      {props.isAuthorized && !props.isFeatureSupported && (
        <Typography
          className='text-xs'
          sx={{
            width: '176px',
            color: 'var(--mui-palette-Tooltip-text)',
          }}
        >
          {`In order to use this feature, update the relevant account's permissions version.`}
        </Typography>
      )}
    </Stack>
  );
};

interface AuthorizationTooltipProps extends Omit<TooltipProps, 'title'> {
  isAuthorized?: boolean;
  isFeatureSupported?: boolean;
  title?: string;
  overrideTitle?: boolean;
}

export const AuthorizationTooltip = (props: AuthorizationTooltipProps) => {
  const {
    children,
    isAuthorized = true,
    isFeatureSupported = true,
    overrideTitle = false,
    ...tooltipProps
  } = props;
  const tooltipTitle =
    props.title || !isAuthorized || !isFeatureSupported ? (
      <TooltipContent
        isAuthorized={isAuthorized}
        isFeatureSupported={isFeatureSupported}
        title={props.title || null}
        overrideTitle={overrideTitle}
      />
    ) : undefined;

  return (
    <Tooltip {...tooltipProps} title={tooltipTitle}>
      <span>{children}</span>
    </Tooltip>
  );
};
